import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import type { AccessMethod, AccessPath, Entrance } from 'ux/Tenant/TenantAdmin/Building/types';
import type { AccessType } from 'ux/Tenant/TenantAdmin/Building/VendorAccess/types';

export type VendorAccessWizardStep = 'address' | 'entrance' | 'access' | 'instructions';

/** Open vendor access wizard session (persists across layout breakpoint / VendorAccess remounts). */
export type VendorAccessWizardSession = {
  buildingId: number;
  tenancyId: number;
  accessType: AccessType;
  accessPath: AccessPath;
  initialStep?: VendorAccessWizardStep;
  initialCreatedEntrance?: Entrance | null;
  entrances: Entrance[];
  existingAccessMethods: AccessMethod[];
};

type VendorAccessWizardState = {
  session: VendorAccessWizardSession | null;
};

const initialState: VendorAccessWizardState = {
  session: null
};

const vendorAccessWizardSlice = createSlice({
  name: 'vendorAccessWizard',
  initialState,
  reducers: {
    openVendorAccessWizardSession: (state, action: PayloadAction<VendorAccessWizardSession>) => {
      state.session = action.payload;
    },
    closeVendorAccessWizardSession: state => {
      state.session = null;
    },
    syncVendorAccessWizardAfterFetch: (
      state,
      action: PayloadAction<{
        buildingId: number;
        paths: AccessPath[];
        entrances: Entrance[];
        existingAccessMethods: AccessMethod[];
      }>
    ) => {
      if (!state.session || state.session.buildingId !== action.payload.buildingId) return;
      const pathId = state.session.accessPath.id;
      const freshPath = action.payload.paths.find(p => p.id === pathId);
      if (freshPath) {
        state.session.accessPath = freshPath;
      }
      state.session.entrances = action.payload.entrances;
      state.session.existingAccessMethods = action.payload.existingAccessMethods;
    }
  }
});

export const { openVendorAccessWizardSession, closeVendorAccessWizardSession, syncVendorAccessWizardAfterFetch } =
  vendorAccessWizardSlice.actions;

export type VendorAccessWizardRootSlice = { vendorAccessWizard: VendorAccessWizardState };

export const selectVendorAccessWizardSession = (state: VendorAccessWizardRootSlice) => state.vendorAccessWizard.session;

export default vendorAccessWizardSlice.reducer;
